import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import styles from '../styles.module.css';
import { PaginationParams, User } from './types';
import UserIcon from '@/assets/icons/user.svg?react';
import {
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import Checkbox from '@/components/Form/Checkbox';
import PlusButton from '@/components/Form/PlusButton';
import SearchInput from '@/components/Form/searchInput';
import Pagination from '@/components/Pagination';
import { PaginationRequestParams } from '@/components/Pagination/usePaginationTokens';
import { adminToolPaths } from '@/constants/adminTool';
import { PermissionsList } from '@/constants/permissionsList';
import { useToast } from '@/context/ToastContext';
import { useGetUsersList } from '@/hooks/services/Users';
import { USER_PAGE_SIZE } from '@/solaris-lens-common-lib/src/constants';
import { RootState } from '@/store';
import { getUsersPermissions } from '@/utils/adminToolPermissions';

function UsersContainer() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canCreate: canCreateUser } = getUsersPermissions(
    auditableElements,
    userGroup
  );

  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    pageSize: USER_PAGE_SIZE,
    paginationToken: undefined,
    method: 'firstPage'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Cognito's users endpoint only exposes a forward cursor, so we keep our
  // own page -> token map to support going back to a previously visited page.
  const pageTokensRef = useRef<Record<number, string | undefined>>({
    1: undefined
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    pageTokensRef.current = { 1: undefined };
    setCurrentPage(1);
    setPaginationParams(prev => ({
      ...prev,
      paginationToken: undefined,
      pageNumber: undefined,
      method: 'firstPage'
    }));
  }, [debouncedSearchTerm]);

  const {
    data: rawResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetUsersList({
    ...paginationParams,
    searchTerm: debouncedSearchTerm,
    searchType: 'qemail'
  });

  useEffect(() => {
    if (!loading && rawResponse && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, rawResponse, isInitialLoad]);

  const users = useMemo(() => rawResponse?.data ?? [], [rawResponse]);

  const error = queryError ? (queryError as Error).message : null;

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const status = user.status || 'Inactive';
      const username = user.username || '';
      const email = user.attributes?.email || '';
      const matchesSearch =
        searchTerm === '' ||
        username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.toLowerCase().includes(searchTerm.toLowerCase());
      const isUserActive = user.enabled?.toString() === 'true';
      const isUserInactive = !isUserActive;
      const matchesStatusFilter =
        (showActive && isUserActive) || (showInactive && isUserInactive);
      return matchesSearch && matchesStatusFilter;
    });
  }, [users, searchTerm, showActive, showInactive]);

  const paginatedUsers = useMemo(() => {
    if (rawResponse?.info?.paginator) return filteredUsers;
    const startIndex = (currentPage - 1) * paginationParams.pageSize;
    return filteredUsers.slice(
      startIndex,
      startIndex + paginationParams.pageSize
    );
  }, [filteredUsers, currentPage, paginationParams.pageSize, rawResponse]);

  useEffect(() => {
    if (rawResponse?.info?.paginator) {
      const token = rawResponse.info.paginator.paginationToken || null;
      // Store the token needed to reach the following page so "previous"
      // navigation can look it up again without refetching from scratch.
      pageTokensRef.current[currentPage + 1] = token || undefined;
      setNextPageToken(token);
      setTotalPages(token ? currentPage + 1 : currentPage);
    } else {
      setTotalPages(
        Math.max(1, Math.ceil(filteredUsers.length / paginationParams.pageSize))
      );
      setNextPageToken(null);
    }
  }, [
    rawResponse,
    filteredUsers.length,
    paginationParams.pageSize,
    currentPage
  ]);

  const handlePageChange = (params: PaginationRequestParams) => {
    if (rawResponse?.info?.paginator) {
      if (params.direction === 'next' && !nextPageToken) return;
      const targetPage = Math.max(params.page, 1);
      setCurrentPage(targetPage);
      setPaginationParams(prev => ({
        ...prev,
        pageSize: params.pageSize,
        paginationToken: pageTokensRef.current[targetPage],
        method: targetPage === 1 ? 'firstPage' : 'token'
      }));
    } else {
      if (params.pageNumber) {
        setCurrentPage(params.pageNumber);
      } else if (params.direction === 'next') {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
      } else if (params.direction === 'previous') {
        setCurrentPage(prev => Math.max(prev - 1, 1));
      }
    }
  };

  const handleUserClick = (username: string) => {
    navigate(adminToolPaths.users.detail.replace(':userId', username));
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  useEffect(() => {
    if (error) {
      showToast(error || 'An error occurred while loading users', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => refetch()
      });
    }
  }, [error]);

  if (loading && isInitialLoad) {
    return <ContainerLoader />;
  }

  const columns: DataTableColumn[] = [
    {
      field: 'username',
      headerName: 'USERNAME',
      width: 'auto',
      sortable: true,
      onClick: (row: User) => {
        handleUserClick(row.username);
      }
    },
    {
      field: 'name',
      headerName: 'NAME',
      width: 'auto',
      sortable: true,
      valueGetter: (_, row: User) => row.attributes?.name || '',
      onClick: (row: User) => {
        handleUserClick(row.username);
      }
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      onClick: (row: User) => {
        handleUserClick(row.username);
      },
      width: 'auto',
      sortable: true,
      valueGetter: (_, row: User) => row.attributes?.email || ''
    },
    {
      field: 'groups',
      headerName: 'USER ROLE',
      valueGetter: (_, row: User) => row.attributes.groups?.join(' / ') || '',
      width: 'auto',
      sortable: true
    }
  ];

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <UserIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>Users</h2>
            </>
          }
          center={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by email"
              />
              <Checkbox
                checked={showActive}
                onChange={setShowActive}
                label="Active"
              />
              <Checkbox
                checked={showInactive}
                onChange={setShowInactive}
                label="Inactive"
              />
            </div>
          }
          right={
            canCreateUser ? (
              <PlusButton
                onClick={() => navigate(adminToolPaths.users.newUser)}
                label="Add a new user"
              />
            ) : null
          }
        />
        <AdminTableBody>
          {loading && <ContainerLoader />}
          {!loading && (
            <>
              <DataTable
                rows={paginatedUsers}
                columns={columns}
                initialState={{ sorting: { field: 'username', sort: 'asc' } }}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                nextPageToken={nextPageToken}
                pageSize={paginationParams.pageSize}
                onPageChange={handlePageChange}
                useTokens={true}
              />
            </>
          )}
        </AdminTableBody>
      </div>
    </div>
  );
}

export default UsersContainer;
